import { FileStore } from "@pdfking/compatibility"
import { AutoThrow, Failure, Success } from "@pdfking/errors"
import type { StandardResponse } from "@pdfking/io"
import { ResponseAdopt } from "@pdfking/io"
import type { FileName } from "@pdfking/structs"
import { ContentType, StringQ } from "@pdfking/structs"
import { RecordMerge } from "@pdfking/tfunc"

import { FILE_STORE_PROTOCOL } from "./Protocols"

export type UrlProtocolHandler = (url: URL, mime: ContentType) => Promise<StandardResponse>

// What a store read comes back as, either way it goes: a url that names
// nothing is a miss carrying the url it could not answer for, raised the same
// way every other failure in the codebase is.
export const UrlHit = (url: URL, handle: StandardResponse | undefined): StandardResponse =>
  AutoThrow(handle ? Success(handle) : Failure("CACHE_MISS", { url: url.href }))

// What a store url says, read back: the authority names the store and the path
// names the entry — the two halves the url was built from, each decoded a
// segment at a time so a name that needed encoding survives the round trip.
export const UrlStoreName = (url: URL): string => decodeURIComponent(url.hostname)

export const UrlStoreKey = (url: URL): string =>
  url.pathname.slice(1).split("/").map(decodeURIComponent).join("/")

// Whatever the network said, read as what it carries: a download's headers are
// where its length, type and version already are.
const UrlFetch: UrlProtocolHandler = url =>
  // The url is the entry's name here: a download has no path in a storage, and
  // what scopes an etag over HTTP is the resource it came from.
  fetch(url, { cache: "force-cache" }).then(response =>
    ResponseAdopt(response, { name: url.href as FileName })
  )

// An `fs:` url is a path in the file store, and what a loader hands back is a
// download — but a stored entry *is* a response, its length, type, etag and
// write time already the headers a loader reads, so there is nothing to build
// here and nothing to copy.
const FileStoreFetch: UrlProtocolHandler = async url =>
  UrlHit(url, await FileStore(UrlStoreName(url)).get(UrlStoreKey(url) as FileName))

// Only the protocols `fetch` itself can't serve. Everything else — http(s),
// blob, and whatever scheme the host happens to serve the app from (`tauri:`
// in a bundled desktop build, and its equivalents elsewhere) — is fetch's job,
// so listing schemes here would only be a way to lock those out.
//
// `fs:` is here rather than per platform because it means the same thing on
// both: the storage it resolves to is the platform's, which is what makes the
// url portable in the first place.
const DEFAULT_PROTOCOLS: Record<string, UrlProtocolHandler> = {
  [FILE_STORE_PROTOCOL]: FileStoreFetch,
}

/** Builds a UrlLoad-shaped function dispatching by URL protocol, merging `protocols` over the built-in `fs:` handler and falling back to `fetch`. */
export const ImplementUrlLoad = (protocols: Record<string, UrlProtocolHandler> = {}) => {
  const handlers = RecordMerge(DEFAULT_PROTOCOLS, protocols)

  return async (
    path: string | URL | Response,
    mime: ContentType = ContentType.BYTES
  ): Promise<StandardResponse> => {
    // A Response handed in was loaded by somebody else — adopted rather than
    // returned, so a caller downstream reads `info` off it like any other.
    if (!StringQ(path) && !(path instanceof URL))
      return ResponseAdopt(path, { name: path.url as FileName })
    const url = new URL(path, globalThis.location?.href)
    return (handlers[url.protocol] ?? UrlFetch)(url, mime)
  }
}
